import React, { useCallback } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import IconToolTip from '../../../../common/IconToolTip';
import OverflowTip from '../../../../common/OverflowTip';
import { amountType, paymentType, TransactionCategory, TransactionPurpose, TransactionTransferType } from '../../walletController';
import { validateResponseDate, validateStringValue } from '../../../../utils/common';
import { DateComponent } from '../../../../common/TableReusableColumns';
import { convertIntoRupee, isMobileView } from '../../../../utils/utils';
import { checkTransactionStatus } from '../../../../actions/wallet';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { TransactionStatus } from '../../../Payments/PaymentController';
import { TicketAgentRole } from '../../../Ticket/TicketController';

const mediumFontCss = 'font-roboto font-medium 2xl:text-font12 text-scogoprimary';
const normalFontCss = 'font-normal 2xl:text-font10 text-font10';

export const iconAndColorLookup = {
    orange: { icon: './img/amount_pending.svg', textCss: 'text-scogoorange', amountPrefixSign: '' },
    red: { icon: './img/amount_debited.svg', textCss: 'text-scogoclosed', amountPrefixSign: '' },
    green: { icon: './img/amount_credited.svg', textCss: 'text-scogoToastSuccessIcon', amountPrefixSign: '+' },
    black: { icon: './img/amount_rejected.svg', textCss: 'text-black', amountPrefixSign: '-' },
    grey: { icon: './img/amount_grey.svg', textCss: 'text-scogo99', amountPrefixSign: '' },
};

export default function Card({ transaction }) {
    const statusColors = {
        INITIATED: "bg-yellow-100 border-yellow-500 text-yellow-700",
        PENDING: "bg-yellow-100 border-yellow-500 text-yellow-700",
        SCHEDULED: "bg-sky-100 border-sky-500 text-sky-700",
        PROCESSING: "bg-yellow-100 border-yellow-500 text-yellow-700",
        SUCCESS: "bg-emerald-100 border-emerald-500 text-emerald-700",
        FAILURE: "bg-red-100 border-red-500 text-red-700",
        REJECTED: "bg-rose-100 border-rose-500 text-rose-700",
    };
    const dispatch = useDispatch();

    const isToShowViewDetailsIcon = transaction?.ticketId;
    const isCustomerViewDetailsEnable = Array.isArray(transaction?.sub_transaction_histories) &&
        transaction.sub_transaction_histories.length > 0;

    // Simplified transaction type determination
    const transactionType = transaction?.type || amountType.grey;
    const transactionTitle = transaction?.title || 'Transaction';

    const getIconAndColorOfTransaction = useCallback((type) => {
        let icon = iconAndColorLookup.grey;
        if (type === amountType.debit) icon = iconAndColorLookup.black;
        else if (type === amountType.credit) icon = iconAndColorLookup.green;
        else if (type === amountType.rejected) icon = iconAndColorLookup.red;
        else if ([amountType.pending, amountType.approval_pending, amountType.awaiting, amountType.locked].includes(type))
            icon = iconAndColorLookup.orange;
        return icon;
    }, []);

    const createdAt = transaction?.created_at && moment(transaction.created_at).isValid()
        ? moment(new Date(validateResponseDate(transaction.created_at))).format('llll')
        : '';

    const getDateToShow = () => {
        let label = '';
        let date = '';
        let color = '';

        if (transaction?.paymentType === paymentType.ticket_payment && transaction?.due_date && moment(transaction.due_date).isValid()) {
            label = 'Due Date';
            date = moment(transaction.due_date).format('llll');
            color = 'text-scogoclosed';
        } else if (transaction?.processed_at && moment(transaction.processed_at).isValid()) {
            label = 'Processed Date';
            date = moment(transaction.processed_at).format('llll');
            color = 'text-scogoorange';
        } else if (['Scheduled', 'Pending'].includes(transaction?.status) && transaction.scheduled_on) {
            label = 'Scheduled Date';
            date = moment(transaction.scheduled_on).format('llll');
            color = 'text-scogoorange';
        }

        if (!date || !label) return null;

        return (
            <div className='flex'>
                <p className={mediumFontCss}>
                    <span className='font-medium'>{label}: &nbsp;</span>
                    <span className={`${normalFontCss} ${color}`}>{date}</span>
                </p>
            </div>
        );
    };

    // Determine transaction label
    let transactionLabel = transactionTitle;
    const iconColor = getIconAndColorOfTransaction(transactionType);

    if (iconColor.amountPrefixSign === '+') {
        transactionLabel = `${transactionTitle} (Added to Wallet)`;
    } else if (iconColor.amountPrefixSign === '-') {
        transactionLabel = `${transactionTitle} (Debited from Wallet)`;
    }

    if (transactionLabel === 'failed' && transaction.reason) {
        transactionLabel = `${transactionLabel} (${transaction.reason})`;
    }

    // Format site ID
    let siteId = validateStringValue(transaction.siteId);
    if (transaction.job_ticket_number) {
        siteId = `${siteId}/${transaction.job_ticket_number}`;
    }

    return (
        <div
            className={`shadow-md rounded-xl my-4 px-6 py-3 border-l-4 ${statusColors[transaction.status] || "border-gray-300 bg-white"
                }`}
        >
            <div className={`flex w-full items-start ${isMobileView ? 'flex-col' : ''}`}>
                <div className={`flex ${isMobileView ? 'flex-col w-full' : 'items-center w-6/12'}`}>
                    {!isMobileView && (
                        <div className='min-w-max mr-3'>
                            <img
                                className='h-10 w-10'
                                src={process.env.PUBLIC_URL + getIconAndColorOfTransaction(transactionType).icon}
                                alt='Transaction icon'
                            />
                        </div>
                    )}



                    <div className={`flex flex-col justify-center ${isMobileView ? 'w-full' : ''}`}>
                        <p className={`${mediumFontCss} font-bold mb-1`}>
                            <OverflowTip
                                textClass={`${mediumFontCss} font-bold`}
                                someLongText={transaction.status === TransactionStatus.FAILURE && transaction?.errors?.length > 0 ? transaction.errors[0] : transaction.statement}
                            />
                        </p>
                        <p className={`${normalFontCss} text-scogogray flex items-center mb-1`}>
                            <IconToolTip title='Created At'>
                                <span>{createdAt}</span>
                            </IconToolTip>
                        </p>
                        {transaction?.ticket?.ticket_number ? <div className='flex mb-1'>
                            <p className={mediumFontCss}>
                                <span className='font-medium text-font10'>Ticket Id: </span>
                                <span className={`${normalFontCss} text-font8`}>
                                    {transaction?.ticket?.ticket_number}
                                </span>
                            </p>
                        </div> : null}
                        <div className='flex mb-1'>
                            <p className={mediumFontCss}>
                                <span className='font-medium text-font10'>Transaction Id: </span>
                                <span className={`${normalFontCss} text-font8`}>
                                    {transaction?.id}
                                    {[paymentType.withdraw].includes(transaction.paymentType) &&
                                        transaction.payment_via
                                        ? ` (${transaction.payment_via})`
                                        : ''}
                                </span>
                            </p>
                        </div>
                        {getDateToShow()}
                        {transaction?.txMsg && (
                            <SpanElement label='Message' value={transaction.remarks} />
                        )}
                    </div>
                </div>

                <div className={`py-2 ${isMobileView ? 'w-full mt-2' : 'w-5/12 flex flex-col'}`}>
                    {siteId && <SpanElement label='Site ID' value={siteId} />}
                    <WalletTranseferPaymentStatus
                        status={transaction.status}
                        id={transaction.id}
                        category={transaction.category}
                        purpose={transaction.purpose}
                        transfer_type={transaction.transfer_type}
                    />
                    {transaction?.external_ticket_id && (
                        <SpanElement label='EXT ID' value={transaction.external_ticket_id} />
                    )}
                    {transaction?.po_number && (
                        <SpanElement label='PO Number' value={transaction.po_number} />
                    )}
                    <div className='flex '>
                        <div >
                            {transaction.updated_at && (
                                <p className={`${mediumFontCss} flex items-center w-full gap-2 mb-1`}>
                                    <span className='text-scogogray text-font10'>Updated At: </span>
                                    <DateComponent
                                        className='font-roboto font-medium text-font10 text-scogoprimary'
                                        date={transaction.updated_at}
                                        text={false}
                                        timeFormat='DD MMM YYYY'
                                    />
                                </p>
                            )}
                            {transaction?.is_amount_refunded === 1 && (
                                <SpanElement label='Refund' value='Amount Refunded To Wallet' />
                            )}
                        </div>
                        {isMobileView && <RenderTransactionAmount
                            getIconAndColorOfTransaction={getIconAndColorOfTransaction}
                            transactionType={transactionType}
                            transaction={transaction}
                            isToShowViewDetailsIcon={isToShowViewDetailsIcon}
                            isCustomerViewDetailsEnable={isCustomerViewDetailsEnable}
                        />}
                    </div>
                </div>

                {!isMobileView && <div className={`${isMobileView ? 'w-full mt-2' : ' mt-2'}`}>
                    <RenderTransactionAmount
                        getIconAndColorOfTransaction={getIconAndColorOfTransaction}
                        transactionType={transactionType}
                        transaction={transaction}
                        isToShowViewDetailsIcon={isToShowViewDetailsIcon}
                        isCustomerViewDetailsEnable={isCustomerViewDetailsEnable}
                    />
                </div>}
            </div>
        </div>


    );
}

const SpanElement = ({ label, value }) => {
    return (
        <p className={`${mediumFontCss} flex w-full gap-2 mb-1`}>
            <OverflowTip
                someLongText={
                    <>
                        <span className='text-scogogray'>{label}: </span>
                        <span className='w-4/6'>{value}</span>
                    </>
                }
            />
        </p>
    );
};

const WalletTranseferPaymentStatus = ({ status, id, category, purpose, transfer_type }) => {
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const getWalletTransferPaymentStatus = (status) => {
        const lookup = {
            'SUCCESS': { label: 'Paid', color: 'green-600' },
            'PENDING': { label: 'Pending', color: 'yellow-600' },
            'SCHEDULED': { label: 'Scheduled', color: 'blue-600' },
            'FAILURE': { label: 'Failed', color: 'red-600' },
            'INITIATED': { label: 'Initiated', color: 'blue-600' },
            'PROCESSING': { label: 'Processing', color: 'blue-600' },
            'REJECTED': { label: 'Rejected', color: 'red-600' },
        };

        return lookup[status] || { label: status, color: 'gray-600' };
    };

    const { label, color } = getWalletTransferPaymentStatus(status);

    const showRefreshIcon = useMemo(() => {
        const isDepositFromBank =
            purpose === TransactionPurpose.DEPOSIT &&
            transfer_type === TransactionTransferType.BANK_TO_WALLET;

        const isWithdrawToBank =
            purpose === TransactionPurpose.WITHDRAW &&
            category === TransactionCategory.WALLET_TO_BANK &&
            transfer_type === TransactionTransferType.WALLET_TO_BANK;

        const isPOForLineItemOrGST =
            purpose === TransactionPurpose.PO &&
            transfer_type === TransactionTransferType.WALLET_TO_BANK &&
            (category === TransactionCategory.LINE_ITEM || category === TransactionCategory.GST);

        const isTransferToBank =
            purpose === TransactionPurpose.TRANSFER &&
            category === TransactionCategory.WALLET_TO_BANK &&
            transfer_type === TransactionTransferType.WALLET_TO_BANK;

        return (
            isDepositFromBank ||
            isWithdrawToBank ||
            isPOForLineItemOrGST ||
            isTransferToBank
        );
    }, [transfer_type, category, purpose]);

    const refreshTransactionStatus = () => {
        dispatch(checkTransactionStatus({ id, formName: `transaction_status_${id}`, source: 'wallet_card' }));
    }

    return (
        <p className={`${mediumFontCss} flex w-full gap-2 mb-1`}>
            <OverflowTip
                someLongText={
                    <div className='flex items-center gap-2'>
                        <span className='text-scogogray text-font10'>Status: </span>
                        <span className={`text-${color} text-font10 font-medium`}>{label}</span>
                        {showRefreshIcon ? <span onClick={refreshTransactionStatus} className={`ml-2 ${formButtonLoading[`transaction_status_${id}`] ? 'animate-spin' : ''} material-icons text-font12 text-scogoorange`}>refresh</span> : null}
                    </div>
                }
            />
        </p>
    );
};

const RenderTransactionAmount = ({ transaction, isToShowViewDetailsIcon, isCustomerViewDetailsEnable }) => {
    const dispatch = useDispatch();

    const onArrowClick = () => {
        // if (isToShowViewDetailsIcon) {
        //     const heading = 'Payments';
        //     const modalComponent = <PaymentsDetails ticketId={transaction?.ticketId} />;
        //     const modalWidth = '90rem';
        //     const modalHeight = 'auto';
        //     dispatch(openCustomModal({ modalComponent, heading, modalWidth, modalHeight }));
        // } else if (isCustomerViewDetailsEnable) {
        //     const heading = 'Transaction Details';
        //     const modalComponent = <ViewDetailsCustomer transaction={transaction} />;
        //     const modalWidth = '80rem';
        //     const modalHeight = 'auto';
        //     dispatch(openCustomModal({ modalComponent, heading, modalWidth, modalHeight }));
        // }
    };

    const getViewDetailsIconElement = () => {
        const isViewDetailsEnable = isToShowViewDetailsIcon || isCustomerViewDetailsEnable;

        if (!isViewDetailsEnable) return null;

        return (
            <IconToolTip title='View Details'>
                <span
                    onClick={onArrowClick}
                    className='text-scogoprimary hover:text-scogoorange cursor-pointer material-icons text-font14 ml-2'
                >
                    arrow_forward_ios
                </span>
            </IconToolTip>
        );
    };


    const amount = getAmount(transaction);

    return (
        <div className='flex items-center justify-end w-2/12'>
            <div className='flex items-center'>
                {amount < 0 && (
                    <div className='text-red-500 font-medium text-lg flex'>
                        <span>  ₹</span><span> {amount}</span>
                    </div>
                )}
                {amount > 0 && (
                    <div className='text-green-500 font-medium text-lg flex flex-row'>
                        <span> ₹</span> <span> {amount}</span>
                    </div>
                )}
                {getViewDetailsIconElement()}
            </div>
        </div>
    );
};


export const getAmount = ({ net_debit, net_credit, credit, debit, amount_type, purpose, ticket_agent_role, category }) => {
    let amount = 0;
    if (net_debit > 0) {
        if (purpose === TransactionPurpose.TRANSFER || amount_type === 'OFFLINE') {
            amount = net_debit;
        } else {
            amount = -net_debit;
        }
    } else if (net_credit > 0) {
        amount = net_credit;
    } else if (credit > 0) {
        amount = credit;
    } else if (debit > 0) {
        if (purpose === TransactionPurpose.TRANSFER || amount_type === 'OFFLINE') {
            amount = debit;
        } else {
            amount = -debit;
        }
    };

    const calculatedAmount = convertIntoRupee(Math.abs(amount)).toFixed(2) * (amount < 0 ? -1 : 1);

    if (ticket_agent_role === TicketAgentRole.CUSTOMER) {
        return -calculatedAmount;
    }

    if (category === TransactionCategory.SETTLEMENT) {
        return Math.abs(calculatedAmount);
    }

    return calculatedAmount;
}